import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import { toLocalDateInput } from '../../utils/localDate'
import {
  HiOutlineCalendar,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineClock,
  HiOutlineUser,
  HiOutlineDocumentText,
  HiOutlineArrowTopRightOnSquare,
} from 'react-icons/hi2'
import { Card, IconButton, EmptyState } from '../../components/ui'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatTime(timeString) {
  if (!timeString) return ''
  return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

// Sessions carry their time inside `date` (an ISO instant). `ttime` is a
// legacy field the web app never sets.
function formatSessionTime(session) {
  if (session?.date) {
    const d = new Date(session.date)
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    }
  }
  return formatTime(session?.ttime)
}

export default function SessionCalendar() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getEntity } = useWorkspace()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState(null)
  const [sessionDocuments, setSessionDocuments] = useState({})
  const [loadingDocuments, setLoadingDocuments] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth()
        const startDate = new Date(year, month, 1)
        const endDate = new Date(year, month + 1, 0)
        const response = await apiService.get('/sessions/', {
          // The server does not filter by these dates yet and pages lists
          // at 10 items, so ask for the full list or sessions go missing.
          params: {
            start_date: toLocalDateInput(startDate),
            end_date: toLocalDateInput(endDate),
            page: 'all',
          },
        })
        if (cancelled) return
        if (response.success) {
          const sessionData = response.data.data?.data || response.data.data || response.data || []
          setSessions(Array.isArray(sessionData) ? sessionData : [])
        } else {
          setSessions([])
        }
      } catch {
        if (!cancelled) setSessions([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [currentDate])

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev)
      newDate.setMonth(prev.getMonth() + direction)
      return newDate
    })
    setSelectedDate(null)
  }

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()
    const days = []
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null)
    for (let day = 1; day <= daysInMonth; day++) days.push(day)
    return days
  }

  const getSessionsForDay = (day) => {
    if (!day || !Array.isArray(sessions)) return []
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    // Compare local calendar days on both sides. Mixing a UTC date with a
    // local one put evening sessions on the next day east of UTC.
    const dateString = toLocalDateInput(new Date(year, month, day))
    return sessions.filter(session => {
      if (!session?.date) return false
      try {
        return toLocalDateInput(session.date) === dateString
      } catch {
        return false
      }
    })
  }

  const fetchDocumentsForSessions = async (sessionsList) => {
    if (!sessionsList || sessionsList.length === 0) {
      setSessionDocuments({})
      return
    }
    setLoadingDocuments(true)
    const documentsMap = {}
    try {
      const results = await Promise.all(sessionsList.map(async (session) => {
        try {
          const response = await apiService.get('/documents/', {
            params: { session_id: session.id, page_size: 100 },
          })
          if (response.success) {
            const responseData = response.data.data || response.data
            const documentsArray = responseData.data || responseData.results || []
            return { sessionId: session.id, documents: documentsArray }
          }
          return { sessionId: session.id, documents: [] }
        } catch {
          return { sessionId: session.id, documents: [] }
        }
      }))
      results.forEach(({ sessionId, documents }) => { documentsMap[sessionId] = documents })
      setSessionDocuments(documentsMap)
    } finally {
      setLoadingDocuments(false)
    }
  }

  const days = getDaysInMonth()
  const today = new Date()
  const isToday = (day) => {
    if (!day) return false
    return today.getFullYear() === currentDate.getFullYear() &&
           today.getMonth() === currentDate.getMonth() &&
           today.getDate() === day
  }
  const isSelected = (day) => {
    if (!day || !selectedDate) return false
    return selectedDate.getDate() === day &&
           selectedDate.getMonth() === currentDate.getMonth() &&
           selectedDate.getFullYear() === currentDate.getFullYear()
  }

  if (loading) {
    return (
      <Card padding="md">
        <Card.Header
          title={
            <span className="inline-flex items-center gap-2">
              <HiOutlineCalendar className="w-5 h-5 text-ink-muted" />
              {t('dashboard.calendar.title', '{{entity}} calendar', { entity: getEntity('session').singular })}
            </span>
          }
        />
        <div className="grid grid-cols-7 gap-2 mb-2">
          {DAY_NAMES.map((_, i) => <div key={i} className="h-4 bg-muted rounded animate-pulse" />)}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 35 }).map((_, i) => <div key={i} className="h-12 bg-muted rounded animate-pulse" />)}
        </div>
      </Card>
    )
  }

  return (
    <Card padding="md">
      <Card.Header>
        <div className="flex items-center justify-between gap-3 w-full">
          <div className="flex items-center gap-2">
            <HiOutlineCalendar className="w-5 h-5 text-ink-muted" />
            <h3 className="text-base font-semibold text-ink-primary">
              {t('dashboard.calendar.title', '{{entity}} calendar', { entity: getEntity('session').singular })}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            <IconButton
              icon={HiOutlineChevronLeft}
              aria-label="Previous month"
              variant="ghost"
              size="sm"
              className="rtl:rotate-180"
              onClick={() => navigateMonth(-1)}
            />
            <span className="text-sm font-medium text-ink-primary px-2 tabular-nums">
              {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
            </span>
            <IconButton
              icon={HiOutlineChevronRight}
              aria-label="Next month"
              variant="ghost"
              size="sm"
              className="rtl:rotate-180"
              onClick={() => navigateMonth(1)}
            />
          </div>
        </div>
      </Card.Header>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {DAY_NAMES.map((day) => (
          <div key={day} className="text-xs font-medium text-ink-muted text-center py-2">{day}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {days.map((day, index) => {
          const daySessions = getSessionsForDay(day)
          const hasMultiple = daySessions.length > 1
          return (
            <div
              key={index}
              className={`
                min-h-[3rem] p-1 rounded-lg border relative transition-colors
                ${day ? 'cursor-pointer hover:bg-muted' : 'cursor-default'}
                ${isToday(day) ? 'bg-brand-soft border-brand/30' : 'border-border-subtle'}
                ${isSelected(day) ? 'bg-info-soft border-info/30' : ''}
                ${daySessions.length > 0 && !isToday(day) && !isSelected(day) ? 'bg-muted/50' : ''}
              `}
              onClick={() => {
                if (!day) return
                const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
                const newSelected = selectedDate && isSelected(day) ? null : clickedDate
                setSelectedDate(newSelected)
                if (newSelected) fetchDocumentsForSessions(daySessions)
                else setSessionDocuments({})
              }}
            >
              {day && (
                <>
                  <div className="flex items-center justify-between">
                    <div className={`text-xs font-medium ${isToday(day) ? 'text-brand' : 'text-ink-secondary'}`}>
                      {day}
                    </div>
                    {daySessions.length > 0 && (
                      <span className="min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-info rounded-full leading-none px-1">
                        {daySessions.length}
                      </span>
                    )}
                  </div>
                  {daySessions.length > 0 && (
                    <div className="mt-1">
                      {hasMultiple ? (
                        <div className="text-[10px] text-info font-medium px-0.5 truncate">
                          {daySessions.length} {getEntity('session').plural.toLowerCase()}
                        </div>
                      ) : (
                        <div className="text-[10px] bg-info-soft text-info px-1 rounded truncate">
                          {daySessions[0].title || getEntity('session').singular}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>

      {/* Selected day sessions */}
      {selectedDate && (
        <div className="mt-6 pt-4 border-t border-border-subtle">
          <h4 className="text-sm font-semibold text-ink-primary mb-3">
            {getEntity('session').plural} on {selectedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </h4>

          {getSessionsForDay(selectedDate.getDate()).length === 0 ? (
            <EmptyState
              icon={HiOutlineCalendar}
              title={t('dashboard.calendar.noSessions', 'No {{entity}} scheduled', { entity: getEntity('session').plural.toLowerCase() })}
              compact
            />
          ) : (
            <ul className="space-y-3">
              {getSessionsForDay(selectedDate.getDate()).map((session) => {
                const documents = sessionDocuments[session.id] || []
                return (
                  <li key={session.id} className="bg-surface border border-border rounded-xl overflow-hidden hover:border-brand/40 transition-colors">
                    <button
                      type="button"
                      onClick={() => navigate(`/sessions/${session.id}`)}
                      className="w-full flex items-center gap-3 p-3 text-start hover:bg-muted/40 transition-colors"
                    >
                      <span className="w-10 h-10 bg-info-soft text-info rounded-lg flex items-center justify-center flex-shrink-0">
                        <HiOutlineClock className="w-5 h-5" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-semibold text-ink-primary truncate">
                          {session.title || `Untitled ${getEntity('session').singular}`}
                        </h5>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted mt-1">
                          <span className="inline-flex items-center gap-1">
                            <HiOutlineClock className="w-3 h-3" />
                            {formatSessionTime(session)}
                          </span>
                          {session.client_name && (
                            <span className="inline-flex items-center gap-1">
                              <HiOutlineUser className="w-3 h-3" />
                              {session.client_name}
                            </span>
                          )}
                          {documents.length > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <HiOutlineDocumentText className="w-3 h-3" />
                              {documents.length}{' '}
                              {documents.length === 1
                                ? getEntity('document').singular.toLowerCase()
                                : getEntity('document').plural.toLowerCase()}
                            </span>
                          )}
                        </div>
                      </div>
                      <HiOutlineArrowTopRightOnSquare className="w-4 h-4 text-ink-muted flex-shrink-0 rtl:rotate-[-90deg]" />
                    </button>

                    {/* Docs */}
                    {loadingDocuments ? (
                      <div className="px-3 pb-3 space-y-2">
                        <div className="h-10 bg-muted rounded-lg animate-pulse" />
                        <div className="h-10 bg-muted rounded-lg animate-pulse" />
                      </div>
                    ) : documents.length > 0 ? (
                      <div className="px-3 pb-3 space-y-1.5 bg-muted/30">
                        <div className="pt-2 text-[10px] font-semibold text-ink-muted uppercase tracking-wider inline-flex items-center gap-1">
                          <HiOutlineDocumentText className="w-3 h-3" />
                          {getEntity('document').plural} ({documents.length})
                        </div>
                        {documents.slice(0, 5).map((doc) => (
                          <button
                            key={doc.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/documents/${doc.id}`)
                            }}
                            className="w-full flex items-center gap-2.5 p-2 bg-surface border border-border rounded-lg hover:border-brand/40 hover:bg-brand-soft/30 transition-colors text-start"
                          >
                            <span className="w-7 h-7 bg-brand-soft text-brand rounded-md flex items-center justify-center flex-shrink-0">
                              <HiOutlineDocumentText className="w-3.5 h-3.5" />
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-ink-primary truncate">
                                {doc.title || doc.file_name || `Untitled ${getEntity('document').singular}`}
                              </p>
                              <p className="text-[11px] text-ink-muted">{formatFileSize(doc.file_size)}</p>
                            </div>
                            <HiOutlineArrowTopRightOnSquare className="w-3.5 h-3.5 text-ink-muted flex-shrink-0 rtl:rotate-[-90deg]" />
                          </button>
                        ))}
                        {documents.length > 5 && (
                          <button
                            type="button"
                            onClick={() => navigate(`/sessions/${session.id}`)}
                            className="w-full text-xs text-brand hover:text-brand-hover font-medium py-1.5 text-center"
                          >
                            View all {documents.length} {getEntity('document').plural.toLowerCase()} →
                          </button>
                        )}
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </Card>
  )
}
